import { getSubdomain } from "../utils/getSubdomain";

const API_BASE = "https://api.fadepilot.com";

// Singleton refresh promise — ensures only one refresh call is in-flight at a time.
// If multiple requests get a 401 simultaneously, they all wait for the same
// refresh instead of each triggering their own, which would invalidate the session.
let _refreshPromise = null;

function refreshTokenOnce(refreshAccessToken) {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = refreshAccessToken().finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

export async function authRequest(
  path,
  options = {},
  accessToken,
  refreshAccessToken,
) {
  try {
    return await apiRequest(path, options, accessToken);
  } catch (error) {
    if (error.message === "Invalid or expired access token.") {
      const newToken = await refreshTokenOnce(refreshAccessToken);
      if (!newToken) throw error; // refresh failed, let caller handle
      return await apiRequest(path, options, newToken); // retry once
    }
    throw error;
  }
}

export async function apiRequest(path, options = {}, accessToken = null) {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      data?.error?.message || data?.data?.message || "Something went wrong.";
    const error = new Error(message);
    error.status = response.status; // let callers branch on HTTP status (e.g. 409 slot conflict)
    error.code = data?.error?.code || data?.meta?.code || null;
    throw error;
  }

  return data;
}

export async function loginRequest(email, password) {
  const body = new URLSearchParams();
  body.append("email", email);
  body.append("password", password);
  body.append("domain", getSubdomain());

  return apiRequest("/auth/login.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export async function signupRequest(
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
  phone,
  inviteToken = null,
) {
  const body = new URLSearchParams();
  body.append("first_name", firstName);
  body.append("last_name", lastName);
  body.append("phone_number", phone);
  body.append("email", email);
  body.append("password", password);
  body.append("confirm_password", confirmPassword);
  body.append("domain", getSubdomain());

  // Only append if it exists
  if (inviteToken) {
    body.append("invite_token", inviteToken);
  }

  return apiRequest("/auth/signup.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export async function refreshRequest() {
  return apiRequest("/auth/refresh.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
}

export async function logoutRequest() {
  return apiRequest("/auth/logout.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
}

export async function fetchTenant(domain) {
  return apiRequest(`/tenants/tenant.php?domain=${domain}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export async function resendVerificationEmailRequest(email) {
  const body = new URLSearchParams();
  body.append("email", email);
  body.append("domain", getSubdomain());

  return apiRequest("/auth/resend-email-verification.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export async function resetPasswordRequest(token, password, confirmPassword) {
  const body = new URLSearchParams();
  body.append("token", token);
  body.append("password", password);
  body.append("confirm_password", confirmPassword);

  return apiRequest("/auth/reset-password.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export async function forgotPasswordRequest(email) {
  const body = new URLSearchParams();
  body.append("email", email);
  body.append("domain", getSubdomain());

  return apiRequest("/auth/forgot-password.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export async function fetchBarbers(accessToken, refreshAccessToken) {
  return authRequest(
    "/barbers/barbers.php",
    { method: "GET" },
    accessToken,
    refreshAccessToken,
  );
}

// Fetch services for a specific barber
export async function fetchBarberServices(
  barberId,
  accessToken,
  refreshAccessToken,
) {
  return authRequest(
    `/barbers/services.php?barber_id=${barberId}`,
    { method: "GET" },
    accessToken,
    refreshAccessToken,
  );
}

// Fetch available time slots
export async function fetchAvailability(
  barberId,
  serviceId,
  date,
  accessToken,
  refreshAccessToken,
) {
  return authRequest(
    `/bookings/availability.php?barber_id=${barberId}&service_id=${serviceId}&date=${date}`,
    { method: "GET" },
    accessToken,
    refreshAccessToken,
  );
}

// Create payment intent (card booking)
export async function createPaymentIntent(
  payload,
  accessToken,
  refreshAccessToken,
) {
  return authRequest(
    "/stripe/create-payment-intent.php",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    accessToken,
    refreshAccessToken,
  );
}

// Create setup intent (cash booking - saves card without charging)
export async function createSetupIntent(
  payload,
  accessToken,
  refreshAccessToken,
) {
  return authRequest(
    "/stripe/create-setup-intent.php",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    accessToken,
    refreshAccessToken,
  );
}

// Cancel a booking
export async function cancelBookingRequest(
  bookingId,
  reason,
  accessToken,
  refreshAccessToken,
) {
  return authRequest(
    "/stripe/cancel-booking.php",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        booking_id: bookingId,
        cancellation_reason: reason,
      }),
    },
    accessToken,
    refreshAccessToken,
  );
}

// Fetch client's bookings
export async function fetchClientBookings(accessToken, refreshAccessToken) {
  return authRequest(
    "/bookings/bookings.php",
    { method: "GET" },
    accessToken,
    refreshAccessToken,
  );
}
